import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCheckSquare, FiAlertTriangle, FiXCircle } = FiIcons;

const FormattingChecks = ({ questions }) => {
  const [formattingIssues, setFormattingIssues] = useState([]);
  const [summary, setSummary] = useState({
    totalIssues: 0,
    missingQuestionMarks: 0,
    longLines: 0,
    unbalancedParentheses: 0,
    inconsistentNumbering: 0
  });

  useEffect(() => {
    if (!questions.length) return;
    
    const issues = questions.map(question => {
      const text = question.text;
      
      // Check for missing question mark
      const endsWithQuestion = /[?][\s"')*]*$/.test(text);
      const containsQuestionWords = /\b(what|who|where|when|why|how|which|is|are|do|does|can|could|would|should|will)\b/i.test(text);
      const missingQuestionMark = containsQuestionWords && !endsWithQuestion;
      
      // Check for long lines
      const lines = text.split('\n');
      const longLines = lines.some(line => line.length > 150);
      
      // Check for unbalanced parentheses
      const openParens = (text.match(/\(/g) || []).length;
      const closeParens = (text.match(/\)/g) || []).length;
      const unbalancedParentheses = openParens !== closeParens;
      
      // Check for inconsistent numbering
      const numberingPattern = text.match(/\b(\d+)[\.\)]/g);
      const hasInconsistentNumbering = numberingPattern && numberingPattern.length > 1 && 
        !numberingPattern.every((num, i, arr) => {
          const current = parseInt(num);
          return i === 0 || current === parseInt(arr[i-1]) + 1;
        });
      
      // Overall status
      const hasIssues = missingQuestionMark || longLines || unbalancedParentheses || hasInconsistentNumbering;
      
      return {
        id: question.id,
        text: text.substring(0, 80) + (text.length > 80 ? '...' : ''),
        missingQuestionMark,
        longLines,
        unbalancedParentheses,
        hasInconsistentNumbering,
        hasIssues
      };
    });
    
    setFormattingIssues(issues);
    
    // Calculate summary
    const totalIssues = issues.filter(issue => issue.hasIssues).length;
    const missingQuestionMarks = issues.filter(issue => issue.missingQuestionMark).length;
    const longLinesCount = issues.filter(issue => issue.longLines).length;
    const unbalancedParenthesesCount = issues.filter(issue => issue.unbalancedParentheses).length;
    const inconsistentNumberingCount = issues.filter(issue => issue.hasInconsistentNumbering).length;
    
    setSummary({
      totalIssues,
      missingQuestionMarks,
      longLines: longLinesCount,
      unbalancedParentheses: unbalancedParenthesesCount,
      inconsistentNumbering: inconsistentNumberingCount
    });
    
  }, [questions]);
  
  // Calculate percentage of questions with issues
  const issuePercentage = questions.length ? 
    Math.round((summary.totalIssues / questions.length) * 100) : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <SafeIcon icon={FiCheckSquare} className="text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-800">Consistency & Formatting Checks</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className={`p-6 rounded-lg ${issuePercentage > 30 ? 'bg-red-50' : issuePercentage > 10 ? 'bg-yellow-50' : 'bg-green-50'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-800">Questions with Issues</h4>
              <p className="text-sm text-gray-600">Formatting problems detected</p>
            </div>
            <div className="text-right">
              <p className={`text-3xl font-bold ${issuePercentage > 30 ? 'text-red-700' : issuePercentage > 10 ? 'text-yellow-700' : 'text-green-700'}`}>
                {summary.totalIssues}/{questions.length}
              </p>
              <p className="text-sm font-medium">{issuePercentage}% of questions</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className={`p-4 rounded-lg ${summary.missingQuestionMarks > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
            <h4 className="font-medium text-gray-800 text-sm mb-1">Missing Question Marks</h4>
            <p className={`text-2xl font-bold ${summary.missingQuestionMarks > 0 ? 'text-yellow-700' : 'text-green-700'}`}>
              {summary.missingQuestionMarks}
            </p>
          </div>
          
          <div className={`p-4 rounded-lg ${summary.longLines > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
            <h4 className="font-medium text-gray-800 text-sm mb-1">Long Lines</h4>
            <p className={`text-2xl font-bold ${summary.longLines > 0 ? 'text-yellow-700' : 'text-green-700'}`}>
              {summary.longLines}
            </p>
          </div>
          
          <div className={`p-4 rounded-lg ${summary.unbalancedParentheses > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
            <h4 className="font-medium text-gray-800 text-sm mb-1">Unbalanced Parentheses</h4>
            <p className={`text-2xl font-bold ${summary.unbalancedParentheses > 0 ? 'text-red-700' : 'text-green-700'}`}>
              {summary.unbalancedParentheses}
            </p>
          </div>
          
          <div className={`p-4 rounded-lg ${summary.inconsistentNumbering > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
            <h4 className="font-medium text-gray-800 text-sm mb-1">Inconsistent Numbering</h4>
            <p className={`text-2xl font-bold ${summary.inconsistentNumbering > 0 ? 'text-yellow-700' : 'text-green-700'}`}>
              {summary.inconsistentNumbering}
            </p>
          </div>
        </div>
      </div>
      
      {summary.totalIssues > 0 && (
        <div className="mt-6">
          <h4 className="font-medium text-gray-800 mb-4">Questions with Formatting Issues</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-gray-50 text-gray-600 text-sm leading-normal">
                  <th className="py-3 px-4 text-left">Question</th>
                  <th className="py-3 px-4 text-center">Missing ?</th>
                  <th className="py-3 px-4 text-center">Long Lines</th>
                  <th className="py-3 px-4 text-center">Unbalanced ()</th>
                  <th className="py-3 px-4 text-center">Bad Numbering</th>
                </tr>
              </thead>
              <tbody className="text-gray-600 text-sm">
                {formattingIssues
                  .filter(issue => issue.hasIssues)
                  .map(issue => (
                    <tr key={issue.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="line-clamp-2">{issue.text}</div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {issue.missingQuestionMark ? (
                          <SafeIcon icon={FiXCircle} className="mx-auto text-red-500" />
                        ) : (
                          <SafeIcon icon={FiCheckSquare} className="mx-auto text-green-500" />
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {issue.longLines ? (
                          <SafeIcon icon={FiAlertTriangle} className="mx-auto text-yellow-500" />
                        ) : (
                          <SafeIcon icon={FiCheckSquare} className="mx-auto text-green-500" />
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {issue.unbalancedParentheses ? (
                          <SafeIcon icon={FiXCircle} className="mx-auto text-red-500" />
                        ) : (
                          <SafeIcon icon={FiCheckSquare} className="mx-auto text-green-500" />
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {issue.hasInconsistentNumbering ? (
                          <SafeIcon icon={FiAlertTriangle} className="mx-auto text-yellow-500" />
                        ) : (
                          <SafeIcon icon={FiCheckSquare} className="mx-auto text-green-500" />
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <div className="mt-8 bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-2">Formatting Best Practices</h4>
        <ul className="text-sm text-gray-600 space-y-2 pl-6 list-disc">
          <li>Questions should end with a question mark</li>
          <li>Keep line length under 150 characters for better readability</li>
          <li>Ensure all parentheses are properly balanced and closed</li>
          <li>When using numbered lists or options, maintain consistent sequential numbering</li>
          <li>Use consistent capitalization and punctuation throughout all questions</li>
          <li>Maintain consistent formatting for similar question types</li>
        </ul>
      </div>
    </div>
  );
};

export default FormattingChecks;